/**
 * Wallpaper Context
 *
 * Provides shared wallpaper state to all components.
 * Fixes the issue where useWallpaper hook creates separate state instances.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import {
  WALLPAPERS,
  DEFAULT_WALLPAPER_ID,
  DEFAULT_ROTATION_INTERVAL,
  type WallpaperConfig,
} from '@/types/wallpaper'

const STORAGE_KEY_WALLPAPER = 'mac-studio-wallpaper'
const STORAGE_KEY_ROTATION = 'mac-studio-wallpaper-rotation'

interface WallpaperContextValue {
  /** Current active wallpaper */
  currentWallpaper: WallpaperConfig
  /** Next wallpaper in rotation (for preloading) */
  nextWallpaper: WallpaperConfig | null
  /** All available wallpapers */
  allWallpapers: WallpaperConfig[]
  /** Set a specific wallpaper by ID */
  setWallpaper: (id: string) => void
  /** Enable/disable rotation */
  setRotationEnabled: (enabled: boolean) => void
  /** Whether rotation is enabled */
  rotationEnabled: boolean
  /** Whether a transition is currently happening */
  isTransitioning: boolean
  /** URL of preloaded next image (if applicable) */
  preloadedImageUrl: string | null
  /** Go to next wallpaper manually */
  nextWallpaperManual: () => void
  /** Go to previous wallpaper manually */
  prevWallpaper: () => void
}

const WallpaperContext = createContext<WallpaperContextValue | null>(null)

interface WallpaperProviderProps {
  children: ReactNode
  /** Rotation interval in milliseconds (default: 5 minutes) */
  rotationInterval?: number
  /** Initial wallpaper ID */
  initialWallpaper?: string
}

export function WallpaperProvider({
  children,
  rotationInterval = DEFAULT_ROTATION_INTERVAL,
  initialWallpaper,
}: WallpaperProviderProps) {
  // Find initial index from saved preference or default
  const getInitialIndex = useCallback(() => {
    if (typeof window === 'undefined') return 0

    const saved = localStorage.getItem(STORAGE_KEY_WALLPAPER)
    if (saved) {
      const index = WALLPAPERS.findIndex(w => w.id === saved)
      if (index >= 0) return index
    }

    if (initialWallpaper) {
      const index = WALLPAPERS.findIndex(w => w.id === initialWallpaper)
      if (index >= 0) return index
    }

    const defaultIndex = WALLPAPERS.findIndex(w => w.id === DEFAULT_WALLPAPER_ID)
    return defaultIndex >= 0 ? defaultIndex : 0
  }, [initialWallpaper])

  const [currentIndex, setCurrentIndex] = useState(getInitialIndex)
  const [rotationEnabled, setRotationEnabledState] = useState(() => {
    if (typeof window === 'undefined') return true
    const saved = localStorage.getItem(STORAGE_KEY_ROTATION)
    return saved !== 'false' // Default to true
  })
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [preloadedImageUrl, setPreloadedImageUrl] = useState<string | null>(null)

  // Current and next wallpaper
  const currentWallpaper = useMemo(() => WALLPAPERS[currentIndex], [currentIndex])
  const nextIndex = useMemo(() => (currentIndex + 1) % WALLPAPERS.length, [currentIndex])
  const nextWallpaper = useMemo(() => WALLPAPERS[nextIndex], [nextIndex])

  // Preload next image
  useEffect(() => {
    if (nextWallpaper.type !== 'image' || !nextWallpaper.src) {
      setPreloadedImageUrl(null)
      return
    }

    const img = new Image()
    img.src = nextWallpaper.src
    img.onload = () => setPreloadedImageUrl(img.src)
    img.onerror = () => setPreloadedImageUrl(null)

    return () => {
      img.onload = null
      img.onerror = null
    }
  }, [nextWallpaper])

  // Rotation timer
  useEffect(() => {
    if (!rotationEnabled) return

    const timer = setInterval(() => {
      setIsTransitioning(true)

      // After transition duration, update the index
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % WALLPAPERS.length)
        setIsTransitioning(false)
      }, 1000) // 1s transition duration
    }, rotationInterval)

    return () => clearInterval(timer)
  }, [rotationEnabled, rotationInterval])

  // Save wallpaper preference
  const setWallpaper = useCallback((id: string) => {
    const index = WALLPAPERS.findIndex(w => w.id === id)
    if (index >= 0) {
      setCurrentIndex(index)
      localStorage.setItem(STORAGE_KEY_WALLPAPER, id)
    }
  }, [])

  // Save rotation preference
  const setRotationEnabled = useCallback((enabled: boolean) => {
    setRotationEnabledState(enabled)
    localStorage.setItem(STORAGE_KEY_ROTATION, String(enabled))
  }, [])

  // Manual navigation
  const nextWallpaperManual = useCallback(() => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % WALLPAPERS.length)
      setIsTransitioning(false)
    }, 1000)
  }, [])

  const prevWallpaper = useCallback(() => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentIndex(prev => (prev - 1 + WALLPAPERS.length) % WALLPAPERS.length)
      setIsTransitioning(false)
    }, 1000)
  }, [])

  // Persist current wallpaper on change
  useEffect(() => {
    if (currentWallpaper) {
      localStorage.setItem(STORAGE_KEY_WALLPAPER, currentWallpaper.id)
    }
  }, [currentWallpaper])

  const value = useMemo(() => ({
    currentWallpaper,
    nextWallpaper,
    allWallpapers: WALLPAPERS,
    setWallpaper,
    setRotationEnabled,
    rotationEnabled,
    isTransitioning,
    preloadedImageUrl,
    nextWallpaperManual,
    prevWallpaper,
  }), [
    currentWallpaper,
    nextWallpaper,
    setWallpaper,
    setRotationEnabled,
    rotationEnabled,
    isTransitioning,
    preloadedImageUrl,
    nextWallpaperManual,
    prevWallpaper,
  ])

  return (
    <WallpaperContext.Provider value={value}>
      {children}
    </WallpaperContext.Provider>
  )
}

export function useWallpaperContext(): WallpaperContextValue {
  const context = useContext(WallpaperContext)
  if (!context) {
    throw new Error('useWallpaperContext must be used within a WallpaperProvider')
  }
  return context
}

/**
 * Safe version that returns null when outside provider.
 * Useful for components that may or may not be in provider.
 */
export function useWallpaperContextSafe(): WallpaperContextValue | null {
  return useContext(WallpaperContext)
}
